import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export interface MandateDetails {
  consumer_name?: string;
  consumer_account?: string;
  consumer_bic?: string;
  card_holder?: string;
  card_number?: string;
  card_label?: string;
  card_expiry_date?: string;
}

export interface CustomerBoMandates {
  mollie_id: string;
  id: number;
  is_primary: boolean;
  status: string;
  method: string;
  details: MandateDetails | string;
}

export type CustomerBoMandatesResponse = BaseResponse<CustomerBoMandates[]>;

export const useGetCustomerBoMandates = (customerId: number) => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  const fetchMandates = async () => {
    const { customerBoMandatesByCustomerId } = GET_URL_NEW({
      offset,
      limit,
      q: qString,
      sort: sortField,
      direction: sortDirection
    });

    const url = customerBoMandatesByCustomerId(customerId);
    const { data } = await Axios.get<CustomerBoMandatesResponse>(url);
    return data;
  };

  return useQuery<CustomerBoMandatesResponse, AxiosError<BaseError>>(
    [
      'customerBoMandates',
      customerId,
      offset,
      limit,
      qString,
      sortField,
      sortDirection
    ],
    fetchMandates,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      onError: error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-has-occurred');
        toast.error(String(msg));
      }
    }
  );
};
